import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { supabase } from '../services/supabase'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import SearchBar from '../components/SearchBar'
import Pagination from '../components/Pagination'
import {
  Building2,
  Search,
  Eye,
  Settings,
  Users,
  Calendar,
  DollarSign,
  TrendingUp,
} from 'lucide-react'
import { getAllOrganizations, updateSubscriptionTier } from '../services/masterAdmin'
import { formatDate } from '../utils/helpers'
import Select from '../components/Select'

const ITEMS_PER_PAGE = 20

const MasterAdminOrganizations = () => {
  const [organizations, setOrganizations] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [showTierModal, setShowTierModal] = useState(false)
  const [availableTiers, setAvailableTiers] = useState([])
  const [tierForm, setTierForm] = useState({
    tier: 'demo',
    trialEndsAt: '',
  })
  const [updating, setUpdating] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    loadOrganizations()
    loadTiers()
  }, [])

  const loadTiers = async () => {
    try {
      // Fetch all subscription tiers from database (including inactive ones for master admin)
      const { data: tiers, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .order('price_monthly', { ascending: true })

      if (error) throw error

      // Convert to options format
      const tierOptions = (tiers || []).map(tier => ({
        value: tier.code,
        label: tier.name,
      }))

      setAvailableTiers(tierOptions)
    } catch (error) {
      console.error('Error loading subscription tiers:', error)
      // Fallback to hardcoded options if database fetch fails
      setAvailableTiers([
        { value: 'demo', label: 'Demo' },
        { value: 'lite', label: 'Lite' },
        { value: 'essential', label: 'Essential' },
        { value: 'professional', label: 'Professional' },
        { value: 'enterprise', label: 'Enterprise' },
        { value: 'white_label', label: 'White Label' },
      ])
    }
  }

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      const data = await getAllOrganizations()
      setOrganizations(data)
    } catch (error) {
      console.error('Error loading organizations:', error)
      alert('Failed to load organizations')
    } finally {
      setLoading(false)
    }
  }

  const filteredOrgs = organizations.filter((org) => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      org.name?.toLowerCase().includes(term) ||
      org.slug?.toLowerCase().includes(term) ||
      org.subscription_tier?.toLowerCase().includes(term)
    )
  })

  const paginatedOrgs = filteredOrgs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )
  const totalPages = Math.ceil(filteredOrgs.length / ITEMS_PER_PAGE)

  const handleUpdateTier = async () => {
    if (!selectedOrg) return
    try {
      setUpdating(true)
      await updateSubscriptionTier(
        selectedOrg.id,
        tierForm.tier,
        tierForm.trialEndsAt || null
      )
      setShowTierModal(false)
      setSelectedOrg(null)
      setTierForm({ tier: 'trial', trialEndsAt: '' })
      loadOrganizations()
      alert('Subscription tier updated successfully!')
    } catch (error) {
      console.error('Error updating tier:', error)
      alert(error.message || 'Failed to update subscription tier')
    } finally {
      setUpdating(false)
    }
  }

  const openTierModal = (org) => {
    setSelectedOrg(org)
    const currentTier = org.subscription_tier || 'demo'
    
    // Ensure current tier is in available tiers list
    if (currentTier && !availableTiers.some(t => t.value === currentTier)) {
      const formattedLabel = currentTier
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      setAvailableTiers(prev => [...prev, { value: currentTier, label: formattedLabel }])
    }
    
    setTierForm({
      tier: currentTier,
      trialEndsAt: org.trial_ends_at
        ? new Date(org.trial_ends_at).toISOString().slice(0, 16)
        : '',
    })
    setShowTierModal(true)
  }

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'lite':
        return 'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-800'
      case 'essential':
        return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
      case 'professional':
        return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
      case 'enterprise':
      case 'growth':
        return 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800'
      case 'white_label':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-800'
      case 'demo':
        return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
    }
  }

  const getTierLabel = (tierCode) => {
    return availableTiers.find(t => t.value === tierCode)?.label || tierCode
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Loading organizations...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <Building2 size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Organizations</h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage all organizations and subscriptions • {organizations.length} total
              </p>
            </div>
          </div>
          <Button variant="secondary" onClick={() => navigate('/master-admin')}>
            Back to Dashboard
          </Button>
        </div>
      </motion.div>

      {/* Search */}
      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search organizations by name, slug, or tier..."
      />

      {/* Organizations List */}
      {paginatedOrgs.length === 0 ? (
        <Card variant="soft" className="p-12 text-center">
          <Building2 size={48} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No organizations found</h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchTerm ? 'Try adjusting your search terms' : 'No organizations have been created yet'}
          </p>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {paginatedOrgs.map((org) => (
              <Card key={org.id} variant="soft" hover className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{org.name}</h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold border ${getTierBadgeColor(
                          org.subscription_tier
                        )}`}
                      >
                        {getTierLabel(org.subscription_tier || 'demo')}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <Users size={16} />
                        <span>{org.organization_members?.[0]?.count || 0} members</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users size={16} />
                        <span>{org.clients?.[0]?.count || 0} clients</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} />
                        <span>Created {formatDate(org.created_at)}</span>
                      </div>
                      {org.trial_ends_at && (
                        <div className="flex items-center gap-2">
                          <DollarSign size={16} />
                          <span>Trial ends {formatDate(org.trial_ends_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/master-admin/organizations/${org.id}`)}
                      className="flex items-center gap-1"
                    >
                      <Eye size={16} />
                      View
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => openTierModal(org)}
                      className="flex items-center gap-1"
                    >
                      <Settings size={16} />
                      Manage
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={filteredOrgs.length}
          />
        </>
      )}

      {/* Tier Update Modal */}
      {showTierModal && selectedOrg && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Update Subscription: {selectedOrg.name}
            </h3>
            <div className="space-y-4">
              <Select
                label="Subscription Tier"
                options={availableTiers}
                value={tierForm.tier}
                onChange={(e) => setTierForm({ ...tierForm, tier: e.target.value })}
                className="w-full"
                placeholder="Select subscription tier"
              />
              <div>
                <Input
                  type="datetime-local"
                  label="Trial Ends At (optional)"
                  value={tierForm.trialEndsAt}
                  onChange={(e) => setTierForm({ ...tierForm, trialEndsAt: e.target.value })}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleUpdateTier}
                  disabled={updating}
                  className="flex-1"
                >
                  {updating ? 'Updating...' : 'Update Subscription'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowTierModal(false)
                    setSelectedOrg(null)
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default MasterAdminOrganizations

